import { useEffect, useRef, useState, useCallback } from 'react';
import { FaTrash } from 'react-icons/fa';
import { apiFetch } from '../api';
import Select from "react-select";

export default function ItemLotScanner({ onItemsUpdate, initialItems , isViewMode}) {
  const [isInitialized, setIsInitialized] = useState(false);
  /* ========================= STATE ========================= */
  const [items, setItems] = useState([]);
  const [scanMode, setScanMode] = useState('ITEM'); // ITEM | LOT
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const [itemList, setItemList] = useState([]);

  /* ========================= ITEM MASTER CACHE (ONLY FOR ITEM CHECK) ========================= */
  const itemCodeSetRef = useRef(new Set());

  /* ========================= SCANNER BUFFER ========================= */
  const bufferRef = useRef('');
  const timeoutRef = useRef(null);
  const processingRef = useRef(false);

  const PAGE_SIZE = 10;

  const [visibleItems, setVisibleItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  // Fetch item list on mount - SINGLE useEffect for this
  useEffect(() => {
    apiFetch('/api/item-master/fetch-item-list').then((data) => {
      const activeItems = data.filter(i => i.status === 0);
      itemCodeSetRef.current = new Set(activeItems.map(i => i.item_code));
      setItemList(activeItems);
    });
  }, []);
  
  // Initialize items and notify parent
  useEffect(() => {
    if (!isInitialized) {
      let initialItemsArray = [];
      
      if (initialItems && Array.isArray(initialItems)) {
        initialItemsArray = initialItems;
      }
      
      setItems(initialItemsArray);
      setIsInitialized(true);
      
      // Notify parent with the initial items
      if (onItemsUpdate) {
        onItemsUpdate(initialItemsArray);
      }
    }
  }, [initialItems, isInitialized, onItemsUpdate]);

  // Notify parent when items change (but not during initialization)
  useEffect(() => {
    if (onItemsUpdate && isInitialized) {
      onItemsUpdate(items);
    }
  }, [items, onItemsUpdate, isInitialized]);

  /* ========================= ITEM SCAN ========================= */
  const processItemScan = useCallback(async (code) => {
    const res = await apiFetch(`/api/item-master/find-item?code=${code}`);
    if (!res?.success) {
      alert('Item not found');
      return;
    }

    setItems((prev) => {
      const updated = [...prev];
      const idx = updated.findIndex(i => i.item_code === res.item_code);
      
      if (idx !== -1) {
        // Existing item → increase qty
        const newQty = updated[idx].qty + 1;
        updated[idx] = {
          ...updated[idx],
          qty: newQty,
          total_weight: newQty * updated[idx].weight,
        };
        setActiveItemIndex(idx);
      } else {
        // New item
        updated.push({
          item_code: res.item_code,
          item_description: res.item_description || res.description || '',
          uom: res.uom || '',
          weight: Number(res.weight) || 0,
          qty: 1,
          total_weight: Number(res.weight) || 0,
          lot_code: '',
        });
        setActiveItemIndex(updated.length - 1);
      }
      return updated;
    });
    setScanMode('LOT');
  }, []);
  /* ========================= LOT SCAN ========================= */
  const processLotScan = useCallback((lotCode) => {
    if (activeItemIndex === null) return;
    
    setItems((prev) => {
      const updated = [...prev];
      const item = updated[activeItemIndex];
      updated[activeItemIndex] = {
        ...item,
        lot_code: item.lot_code ? `${item.lot_code}, ${lotCode}` : lotCode,
      };
      return updated;
    });
    setActiveItemIndex(null);
    setScanMode('ITEM');
  }, [activeItemIndex]);

  /* ========================= REMOVE ITEM ========================= */
  const removeItem = useCallback((index) => {
    setItems((prev) => {
      const updated = prev.filter((_, idx) => idx !== index);
      return updated;
    });
    
    // Reset active item if it was the one being removed
    if (activeItemIndex === index) {
      setActiveItemIndex(null);
      setScanMode('ITEM');
    } else if (activeItemIndex !== null && activeItemIndex > index) {
      // Adjust active index if an item before it was removed
      setActiveItemIndex(activeItemIndex - 1);
    }
  }, [activeItemIndex]);

  /* ========================= BARCODE LISTENER ========================= */
  useEffect(() => {
    const handler = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      
      if (e.key.length === 1) bufferRef.current += e.key;
      
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(async () => {
        if (processingRef.current) return;
        processingRef.current = true;
        
        const code = bufferRef.current.trim();
        bufferRef.current = '';
        
        if (code.length < 3) {
          processingRef.current = false;
          return;
        }
        
        const isItem = itemCodeSetRef.current.has(code);
        
        try {
          if (isItem) {
            setActiveItemIndex(null);
            setScanMode('ITEM');
            await processItemScan(code);
          } else {
            if (scanMode === 'LOT' && activeItemIndex !== null) {
              processLotScan(code);
            }
          }
        } finally {
          processingRef.current = false;
        }
      }, 80);
    };
    
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      clearTimeout(timeoutRef.current);
    };
  }, [scanMode, activeItemIndex, processItemScan, processLotScan]);


  const addManualItem = () => {
    setItems((prev) => [
      ...prev,
      {
        item_code: "",
        item_description: "",
        test_type: "",
        uom: "",
        weight: 0,
        qty: 1,
        total_weight: 0,
        lot_code: "",
        is_manual: true,
      },
    ]);
  };


  const handleManualQtyChange = (index, qty) => {
    setItems((prev) => {
      const updated = [...prev];

      const newQty = parseInt(qty) || 1;

      updated[index] = {
        ...updated[index],
        qty: newQty,
        total_weight: updated[index].weight * newQty,
      };

      return updated;
    });
  };

  const handleManualLotChange = (index, value) => {
    setItems((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        lot_code: value,
      };

      return updated;
    });
  };

  useEffect(() => {
    const filtered = itemList.filter((i) =>
      i.item_code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const end = page * PAGE_SIZE;

    const nextItems = filtered.slice(0, end);

    console.log("Total items:", itemList.length);
    console.log("Filtered items:", filtered.length);
    console.log("Items rendering:", nextItems);

    setVisibleItems(
      nextItems.map((i) => ({
        value: i.item_code,
        label: i.item_code,
      }))
    );
  }, [itemList, page, searchTerm]);

  const handleScrollBottom = () => {
    const filtered = itemList.filter((i) =>
      i.item_code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (page * PAGE_SIZE < filtered.length) {
      setPage((prev) => prev + 1);
    }
  };


  const handleManualItemChange = (index, code) => {
    const item = itemList.find((i) => i.item_code === code);
    if (!item) return;

    setItems((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        item_code: item.item_code,
        item_description: item.name || "",
        test_type: item.test_type || "",
        uom: item.uom || "",
        weight: Number(item.net_weight_per_unit) || 0,
        total_weight:
          (Number(item.net_weight_per_unit) || 0) *
          (updated[index].qty || 1),
      };

      return updated;
    });
  };
  
  /* ========================= RENDER ========================= */
  return (
    <div style={styles.pageContainer}>
      <div style={styles.headerSection}>
        <div style={styles.scanStatusContainer}>
          <div style={styles.scanStatus}>
            <div style={styles.scanStatusLabel}>Current Mode:</div>
            <div style={scanMode === 'ITEM' ? styles.scanModeActive : styles.scanModeInactive}>
              {scanMode === 'ITEM' ? 'SCAN ITEM' : 'SCAN LOT'}
            </div>
          </div>
          <div style={styles.scanInfo}>
            <div style={styles.scanInfoText}>
              <div style={styles.statusDot}></div>
              Scanner Active
            </div>
            {activeItemIndex !== null && (
              <div style={styles.scanInfoText}>
                Active Item: <span style={styles.activeItemBadge}>{items[activeItemIndex]?.item_code}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.cardTitle}>
          <h3>Scanned Items</h3>
          <div style={styles.itemCountBadge}>
            {items.length} Item{items.length !== 1 ? 's' : ''}
          </div>
          </div>
           <button
              onClick={addManualItem}
              style={{
                padding: '8px 16px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                width: '180px'
              }}
            >
              + Add Item Manually
            </button>
        </div>

        {items.length > 0 ? (
          <div style={styles.tableContainer}>
            {/* <table style={styles.tableStyle}>
              <thead>
                <tr>
                  <th style={styles.thStyle}>Sr. No</th>
                  <th style={styles.thStyle}>Item Code</th>
                  <th style={styles.thStyle}>Description</th>
                  <th style={styles.thStyle}>UOM</th>
                  <th style={styles.thStyle}>Qty</th>
                  <th style={styles.thStyle}>Lot Code</th>
                  <th style={styles.thStyle}>Total Weight</th>
                  {!isViewMode && <th style={styles.thStyle}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((i, idx) => (
                  <tr key={idx} style={styles.trStyle(activeItemIndex === idx)}>
                    <td style={styles.tdStyle}>{idx + 1}</td>
                    <td style={styles.tdStyle}>
                      <div style={styles.itemCodeCell}>
                        {i.item_code}
                        {activeItemIndex === idx && (
                          <div style={styles.activeIndicator}>Active</div>
                        )}
                      </div>
                    </td>
                    <td style={styles.tdStyle}>{i.item_description || '-'}</td>
                    <td style={styles.tdStyle}>{i.uom || '-'}</td>
                    <td style={styles.tdStyle}>
                      <span style={styles.qtyBadge}>{i.qty}</span>
                    </td>
                    <td style={styles.tdStyle}>
                      <div style={styles.lotDisplay(i.lot_code)}>
                        {i.lot_code || 'No lot scanned'}
                      </div>
                    </td>
                    <td style={styles.tdStyle}>
                      <span style={styles.weightBadge}>
                        {i.total_weight.toFixed(2)}
                      </span>
                    </td>
                    {!isViewMode && (
                      <td style={styles.tdStyle}>
                        <button
                          style={styles.removeButton}
                          onClick={() => removeItem(idx)}
                          title="Remove item"
                        >
                          <FaTrash />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table> */}
             <table style={styles.tableStyle}>
                          <thead>
                            <tr>
                              <th style={styles.thStyle}>Sr. No</th>
                              <th style={styles.thStyle}>Item Code</th>
                              <th style={styles.thStyle}>Item Description</th>
                              <th style={styles.thStyle}>UMO</th>
                              <th style={styles.thStyle}>Qty</th>
                              <th style={styles.thStyle}>Lot Code / Serial Nos.</th>
                              <th style={styles.thStyle}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((i, idx) => (
                              <tr key={idx} style={styles.trStyle(idx === activeItemIndex)}>
                                <td style={styles.tdStyle}><b>{idx + 1}</b></td>
                               <td style={styles.tdStyle}>
                                  {i.is_manual && !i.item_code ? (
                                    <Select
                                      options={visibleItems}
                                      value={
                                        i.item_code
                                          ? { value: i.item_code, label: i.item_code }
                                          : null
                                      }
                                      onChange={(selected) =>
                                        handleManualItemChange(idx, selected.value)
                                      }
                                      onMenuScrollToBottom={handleScrollBottom}
                                      onInputChange={(input) => {
                                        setSearchTerm(input);
                                        setPage(1);
                                      }}
                                      placeholder="Search Item..."
                                      menuPortalTarget={document.body}
                                      styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                      }}
                                    />
                                  ) : (
                                    <b>{i.item_code}</b>
                                  )}
                                </td>
                  
                                <td style={styles.tdStyle}>
                                  <div>{i.item_description}</div>
                                  {i.test_type && (
                                    <div style={{
                                      fontSize: '12px',
                                      color: '#6b7280',
                                      fontStyle: 'italic',
                                      marginTop: '4px'
                                    }}>
                                      ({i.test_type})
                                    </div>
                                  )}
                                </td>
                                 <td style={styles.tdStyle}>{i.uom || '-'}</td>
                                <td style={styles.tdStyle}>
                                  {i.is_manual ? (
                                    <input
                                      type="number"
                                      min="1"
                                      value={i.qty}
                                      onChange={(e) => handleManualQtyChange(idx, e.target.value)}
                                      style={{
                                        width: '60px',
                                        padding: '4px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        textAlign: 'center',
                                      }}
                                    />
                                  ) : (
                                    i.qty
                                  )}
                                </td>
                                <td style={styles.tdStyle}>
                                  {i.is_manual ? (
                                    <input
                                      type="text"
                                      value={i.lot_code}
                                      onChange={(e) => handleManualLotChange(idx, e.target.value)}
                                      placeholder="Enter lot code"
                                      style={{
                                        width: '100%',
                                        padding: '6px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                      }}
                                    />
                                  ) : (
                                    i.lot_code || <i style={{ color: '#9ca3af' }}>Scan lot code</i>
                                  )}
                                </td>
                                <td style={styles.tdStyle}>
                                  <button
                                    onClick={() => removeItem(idx)}
                                    style={{
                                       padding: '8px 12px',
                                        backgroundColor: '#dc2626',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px',
                                    }}
                                  >
                                      <FaTrash />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
          </div>
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📦</div>
            <h4 style={styles.emptyTitle}>No Items Scanned</h4>
            <p style={styles.emptySubtext}>
              Focus this page and start scanning item barcodes
            </p>
            <div style={styles.scanInstructions}>
              <div style={styles.instruction}>
                <span style={styles.instructionStep}>1.</span>
                Scan an item barcode
              </div>
              <div style={styles.instruction}>
                <span style={styles.instructionStep}>2.</span>
                Scan a lot barcode for that item
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================
   STYLES FOR ITEM LOT SCANNER
   ========================= */
const styles = {
  pageContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '20px',
    width: '100%',
    overflowX: 'auto',
  },
  
  headerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '30px',
    width: '100%',
    gap: '20px',
    flexWrap: 'wrap',
  },
  
  pageTitle: {
    margin: 0,
    fontSize: '28px',
    color: '#1f2937',
    fontWeight: '600',
  },
  
  scanStatusContainer: {
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    padding: '20px',
    minWidth: '300px',
  },
  
  scanStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
  },
  
  scanStatusLabel: {
    fontWeight: '600',
    color: '#475569',
    fontSize: '14px',
  },
  
  scanModeActive: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    color: 'white',
    borderRadius: '8px',
    fontWeight: 'bold',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  
  scanModeInactive: {
    padding: '8px 16px',
    backgroundColor: '#f1f5f9',
    color: '#64748b',
    borderRadius: '8px',
    fontWeight: '500',
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  
  scanInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    fontSize: '14px',
    color: '#475569',
    flexWrap: 'wrap',
  },
  
  scanInfoText: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#22c55e',
    animation: 'pulse 2s infinite',
  },
  
  activeItemBadge: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    padding: '4px 12px',
    borderRadius: '6px',
    fontWeight: '600',
    fontSize: '14px',
  },
  
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '28px',
    marginBottom: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb',
  },
  
  cardHeader: {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  flexWrap: 'wrap',
  gap: '12px',
  },
  
  cardTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#1f2937',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  
  itemCountBadge: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    padding: '6px 12px',
    borderRadius: '8px',
  },
  
  tableContainer: {
    overflowX: 'auto',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  
  tableStyle: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '800px',
  },
  
  thStyle: {
    background: '#f9fafb',
    padding: '16px 12px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#6b7280',
    borderBottom: '2px solid #e5e7eb',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  
  tdStyle: {
    padding: '16px 12px',
    fontSize: '14px',
    color: '#374151',
    borderBottom: '1px solid #f3f4f6',
  },
  
  trStyle: (isActive) => ({
    backgroundColor: isActive ? '#f0f9ff' : 'white',
    borderLeft: isActive ? '4px solid #3b82f6' : 'none',
  }),
  
  itemCodeCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  
  activeIndicator: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#1d4ed8',
    backgroundColor: '#dbeafe',
    padding: '2px 8px',
    borderRadius: '4px',
  },
  
  qtyBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    background: '#dbeafe',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e40af',
    minWidth: '40px',
    textAlign: 'center',
  },
  
  lotDisplay: (hasLot) => ({
    display: 'inline-block',
    padding: hasLot ? '8px 12px' : '8px',
    background: hasLot ? '#e0e7ff' : '#f9fafb',
    borderRadius: '6px',
    fontSize: '13px',
    color: hasLot ? '#3730a3' : '#9ca3af',
    fontStyle: hasLot ? 'normal' : 'italic',
    border: hasLot ? '1px solid #c7d2fe' : '1px solid #e5e7eb',
  }),
  
  weightBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    background: '#f0f9ff',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#0c4a6e',
  },
  
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#9ca3af',
  },
  
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#6b7280',
    marginBottom: '8px',
  },
  
  emptySubtext: {
    fontSize: '14px',
    color: '#9ca3af',
    marginBottom: '24px',
  },
  
  scanInstructions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    maxWidth: '400px',
    margin: '0 auto',
  },
  
  instruction: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  
  instructionStep: {
    width: '24px',
    height: '24px',
    backgroundColor: '#3b82f6',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
  },
  
  manualControls: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  
  controlButton: {
    padding: '12px 24px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  
  clearButton: {
    padding: '12px 24px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  
  removeButton: {
    padding: '8px 12px',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.5,
    },
  },
};

// Add hover effects
styles.controlButton = {
  ...styles.controlButton,
  ':hover': {
    backgroundColor: '#e5e7eb',
  },
};

styles.clearButton = {
  ...styles.clearButton,
  ':hover': {
    backgroundColor: '#fecaca',
  },
};